import { X } from 'lucide-react';
import { initialsOf, hashHue } from '@/utils/colors';
import type { GameFormat, ID } from '@/types';
import {
  ddmmyy, formatName, gameDuration, gameTitle, roleLabel, timeRange, type WorkloadGame,
} from '@/utils/workload';

/**
 * Сам список отработанных игр (строка = игра). Один и тот же в двух местах:
 *  • «Персонал → Загрузка» — всплывающим окном по клику на сотрудника (смотрит кассир/управляющий);
 *  • «Мои игры» — целым экраном у самого сотрудника.
 * Клик по строке открывает карточку игры.
 */
export function WorkloadGamesList({ items, formats, tzShiftMs, onOpenDeal, emptyText = 'За этот месяц игр нет', payByDeal, payReasonByDeal }: {
  items: WorkloadGame[];
  formats: GameFormat[];
  tzShiftMs: number;
  onOpenDeal: (id: ID) => void;
  emptyText?: string;
  /**
   * Своя зарплата за игру (только «Мои игры» — передаётся явно, в общей «Загрузке» для
   * кассира/управляющего не передаётся, колонка не показывается). null = не посчиталось
   * (не хватает данных — уровень/ставка/формат и т.п.), значение не задано у игры — не
   * показываем ничего (ещё не загрузили).
   */
  payByDeal?: Map<string, number | null>;
  /** Причина «не посчиталось» по игре — показываем ВИДИМЫМ текстом, не прячем в title. */
  payReasonByDeal?: Map<string, string | undefined>;
}) {
  if (items.length === 0) {
    return <div className="py-10 text-center text-ink-3 italic">{emptyText}</div>;
  }
  return (
    <div className="min-w-[720px] sm:min-w-0 divide-y divide-line/60">
      {items.map((it, i) => {
        const d = it.deal;
        const ts = d.event.start_at ?? d.event.event_date;
        // Часы считаем ТОЛЬКО по времени игры. Если оно не заполнено или заполнено с
        // ошибкой — ставим прочерк с пометкой «?», а не выдуманное число.
        const dur = gameDuration(d);
        const hrs = dur.minutes / 60;
        const unresolvedReason = payByDeal?.has(d.id) && payByDeal.get(d.id) == null ? (payReasonByDeal?.get(d.id) ?? 'не хватает данных') : null;
        return (
          <div key={`${d.id}-${i}`}>
            <button onClick={() => onOpenDeal(d.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-2 transition-colors text-left rounded-lg">
              <span className="text-xs text-ink-3 tabular-nums w-6 flex-shrink-0">{i + 1}</span>
              <span className="text-xs text-ink-2 tabular-nums w-[68px] flex-shrink-0">{ts != null ? ddmmyy(ts + tzShiftMs) : '—'}</span>
              <span className="text-xs text-ink-2 tabular-nums w-[96px] flex-shrink-0">{timeRange(d, tzShiftMs)}</span>
              <span className="text-sm text-accent font-medium tabular-nums w-[72px] flex-shrink-0">#{d.number}</span>
              <span className="text-sm text-ink-1 flex-1 truncate">{gameTitle(d)}</span>
              <span className="text-xs text-ink-2 w-[110px] flex-shrink-0 truncate">{formatName(formats, d) || '—'}</span>
              <span className="text-xs text-ink-2 tabular-nums w-[52px] flex-shrink-0 text-right">
                {hrs > 0 ? `${hrs.toFixed(1)} ч` : '—'}
                {dur.suspicious && (
                  <span className="text-status-refused ml-0.5"
                    title="Время игры не заполнено или заполнено с ошибкой (например, окончание раньше начала) — часы посчитать нельзя, проверьте карточку">?</span>
                )}
              </span>
              <span className="chip !h-5 !text-[10px] !px-1.5 w-[92px] flex-shrink-0 truncate justify-center">{roleLabel(it.role)}</span>
              {payByDeal && (
                <span className="text-xs tabular-nums w-[80px] flex-shrink-0 text-right font-semibold">
                  {payByDeal.has(d.id)
                    ? (payByDeal.get(d.id) != null ? `${payByDeal.get(d.id)!.toLocaleString('ru-RU')} ₸` : <span className="text-ink-3 font-normal">—</span>)
                    : <span className="text-ink-3">…</span>}
                </span>
              )}
            </button>
            {unresolvedReason && (
              <div className="pl-12 pr-3 pb-1.5 -mt-1 text-[11px] text-amber-600">не посчиталось — {unresolvedReason}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Список игр одного сотрудника за месяц. Нужен, чтобы ведущий и кассир в конце месяца
 * смотрели в ОДИН список, а не сверяли свои записи вручную. Клик по строке открывает
 * карточку игры (стрелки «след./пред.» в карточке ходят по этому же списку).
 */
export function WorkloadGamesModal({ name, role, hours, items, periodText, branchText, formats, tzShiftMs, onOpenDeal, onClose, payByDeal, payReasonByDeal, payTotal }: {
  name: string; role: string; hours: number; items: WorkloadGame[];
  periodText: string; branchText: string; formats: GameFormat[]; tzShiftMs: number;
  onOpenDeal: (id: ID) => void; onClose: () => void;
  /** Зарплата за игры — только когда у смотрящего есть право «Ставки» (финансист/руководитель/супер-админ). */
  payByDeal?: Map<string, number | null>;
  payReasonByDeal?: Map<string, string | undefined>;
  payTotal?: number | null;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[920px] max-h-[92vh] flex flex-col panel shadow-pop animate-pop">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-line">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl text-white grid place-items-center flex-shrink-0 font-bold text-xs"
              style={{ backgroundColor: `hsl(${hashHue(name)}, 55%, 48%)` }}>
              {initialsOf(name)}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-ink-1 leading-tight truncate">{name}</h3>
              <p className="text-xs text-ink-3 mt-0.5 truncate">
                {role} · {periodText} · {branchText} · {items.length} игр · {hours.toFixed(1)} ч
                {payTotal != null && <> · <span className="font-semibold text-ink-1">{payTotal.toLocaleString('ru-RU')} ₸</span></>}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="icon-btn !w-9 !h-9 rounded-full flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-auto p-2 sm:p-3">
          <WorkloadGamesList items={items} formats={formats} tzShiftMs={tzShiftMs} onOpenDeal={onOpenDeal} payByDeal={payByDeal} payReasonByDeal={payReasonByDeal} />
        </div>
      </div>
    </div>
  );
}
